import { useState } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
// Constants
import { isActiveRoute, isActiveRouteSection } from 'helpers/routes';
// Ui
import MillionsIcon, { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './NavigationItem.module.scss';

type LinksWithSubMenu = {
  icon: IconNames;
  label: string;
  href?: never;
  subMenu: {
    label: string;
    href: string;
  }[];
  fillIcon?: boolean;
  badgeCount?: number;
  activeHrefs?: never;
};

type LinksWithoutSubMenu = {
  icon: IconNames;
  label: string;
  href: string;
  subMenu?: never;
  fillIcon?: boolean;
  badgeCount?: number;
  /**
   * Extra routes (and their subroutes) that should also mark this item active.
   */
  activeHrefs?: string[];
};

export type NavigationLink = LinksWithSubMenu | LinksWithoutSubMenu;

type NavigationItemProps = {
  item: NavigationLink;
};

const NavigationItem = ({ item }: NavigationItemProps) => {
  const { pathname } = useRouter();

  const {
    label,
    href,
    icon,
    subMenu,
    fillIcon,
    badgeCount,
    activeHrefs,
  } = item;
  const hasBadge = typeof badgeCount === 'number' && badgeCount > 0;
  const badgeLabel = hasBadge && badgeCount > 99 ? '99+' : `${badgeCount}`;

  const isActiveLink = href
    ? activeHrefs?.length
      ? isActiveRouteSection(pathname, [href, ...activeHrefs])
      : isActiveRoute(pathname, href)
    : false;

  const isActiveParentMenuLink = !!subMenu?.some((item) =>
    isActiveRoute(pathname, item.href)
  );

  const [isSubMenuVisible, setSubMenuVisibility] = useState(
    isActiveParentMenuLink
  );

  const handleSubMenuToggle = () => {
    setSubMenuVisibility((prev) => !prev);
  };

  return (
    <>
      {!!href && (
        <li
          className={cn(styles.listItem, {
            [styles.active]: isActiveLink,
          })}
        >
          <Link href={href}>
            <a
              className={styles.link}
              aria-current={isActiveLink ? 'page' : undefined}
            >
              <MillionsIcon
                name={icon}
                className={cn(styles.icon, {
                  [styles.iconFill]: fillIcon,
                })}
              />
              <span className={styles.label}>{label}</span>
              {hasBadge && <span className={styles.badge}>{badgeLabel}</span>}
            </a>
          </Link>
        </li>
      )}

      {!!subMenu && (
        <li
          className={cn(styles.listItem, {
            [styles.active]: isActiveParentMenuLink,
          })}
        >
          <button className={styles.link} onClick={handleSubMenuToggle}>
            <MillionsIcon
              name={icon}
              className={cn(styles.icon, {
                [styles.iconFill]: fillIcon,
              })}
            />
            <span className={styles.label}>{label}</span>
            <MillionsIcon
              name={isSubMenuVisible ? 'chevronUp' : 'chevronDown'}
              className={styles.arrowIcon}
            />
          </button>

          {isSubMenuVisible && (
            <ul className={styles.subMenu}>
              {subMenu.map((item) => {
                return (
                  <li key={item.label} className={styles.subMenuListItem}>
                    <Link href={item.href}>
                      <a
                        className={cn(styles.subMenuLink, {
                          [styles.active]: isActiveRoute(pathname, item.href),
                        })}
                      >
                        {item.label}
                      </a>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      )}
    </>
  );
};

export default NavigationItem;
